import type { SupabaseClient } from '@supabase/supabase-js';

import { getLogger } from '@kit/shared/logger';
import type { Database } from '@kit/supabase/database';

/**
 * Check if a dancer has completed their profile setup
 * This function can be used in middleware to determine if dancers should be redirected
 * to the profile setup page
 */
export async function checkDancerProfileCompletion(
  supabase: SupabaseClient<Database>,
  userId: string,
): Promise<{
  isCompleted: boolean;
  hasProfile: boolean;
  completionPercentage?: number;
}> {
  try {
    // Check if user has a profile and professional profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('profile_completion_percentage, profile_completed_at')
      .eq('id', userId)
      .single();

    if (profileError || !profile) {
      return {
        isCompleted: false,
        hasProfile: false,
      };
    }

    // Check if user has a professional profile set up as a dancer
    const { data: professionalProfile, error: profError } = await supabase
      .from('professional_profiles')
      .select('id, profile_type')
      .eq('user_id', userId)
      .eq('profile_type', 'dancer')
      .single();

    if (profError || !professionalProfile) {
      return {
        isCompleted: false,
        hasProfile: true,
        completionPercentage: profile.profile_completion_percentage || 0,
      };
    }

    // Consider profile completed if:
    // 1. profile_completed_at is set, OR
    // 2. completion percentage is >= 70%
    const isCompleted = Boolean(
      profile.profile_completed_at ||
      (profile.profile_completion_percentage &&
        profile.profile_completion_percentage >= 70),
    );

    return {
      isCompleted,
      hasProfile: true,
      completionPercentage: profile.profile_completion_percentage || 0,
    };
  } catch (error) {
    const logger = await getLogger();
    logger.error(
      {
        service: 'dancer-profile',
        action: 'check-completion',
        userId,
        error: error instanceof Error ? error.message : String(error),
      },
      'Error checking dancer profile completion',
    );

    return {
      isCompleted: false,
      hasProfile: false,
    };
  }
}

/**
 * Determine if a user is a dancer based on their professional profile
 */
export async function isDancerUser(
  supabase: SupabaseClient<Database>,
  userId: string,
): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('professional_profiles')
      .select('profile_type')
      .eq('user_id', userId)
      .eq('profile_type', 'dancer')
      .single();

    return !error && Boolean(data);
  } catch {
    return false;
  }
}
